/**
 * AI / per-drop creative UI state for Edit Sent modal (mirrors bulk edit creative).
 */

import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';

import {
  MAX_SELECTED_TEXTS,
  uniqueKeepOrderStrings,
} from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';

export function useEditSentDropCreativeAiState(
  dropsKey: string,
  selectedSenderNames: string[],
  setSelectedSenderNames: Dispatch<SetStateAction<string[]>>,
  selectedSmsTexts: string[],
  setSelectedSmsTexts: Dispatch<SetStateAction<string[]>>,
  setLocalSenders: Dispatch<SetStateAction<string[]>>,
  setLocalTexts: Dispatch<SetStateAction<string[]>>,
) {
  const [showSenders, setShowSenders] = useState(true);
  const [showTexts, setShowTexts] = useState(true);
  const [pendingHideSenders, setPendingHideSenders] = useState<string[]>([]);
  const [replacedByAiSenders, setReplacedByAiSenders] = useState<string[]>([]);
  const [pendingHideTexts, setPendingHideTexts] = useState<string[]>([]);
  const [replacedByAiTexts, setReplacedByAiTexts] = useState<string[]>([]);
  const [perDropSmsTexts, setPerDropSmsTexts] = useState<string[][] | null>(null);
  const [perDropSenderNames, setPerDropSenderNames] = useState<string[][] | null>(null);

  useEffect(() => {
    setPerDropSmsTexts(null);
    setPerDropSenderNames(null);
    setPendingHideSenders([]);
    setReplacedByAiSenders([]);
    setPendingHideTexts([]);
    setReplacedByAiTexts([]);
  }, [dropsKey]);

  const markSendersForHide = useCallback((senders: string[]) => {
    setPendingHideSenders((p) => [...p, ...senders]);
  }, []);

  const saveGeneratedSendersAndSelectOnly = useCallback(
    (newSenders: string[]) => {
      setLocalSenders((prev) => uniqueKeepOrderStrings([...prev, ...newSenders]));
      setReplacedByAiSenders((prev) => uniqueKeepOrderStrings([...prev, ...selectedSenderNames]));
      setSelectedSenderNames(newSenders.slice(0, 50));
      setPerDropSenderNames(null);
    },
    [selectedSenderNames, setLocalSenders, setSelectedSenderNames],
  );

  const markTextsForHide = useCallback((texts: string[]) => {
    setPendingHideTexts((p) => [...p, ...texts]);
  }, []);

  const saveGeneratedAndSelectOnly = useCallback(
    (newTexts: string[]) => {
      setLocalTexts((prev) => uniqueKeepOrderStrings([...prev, ...newTexts]));
      setReplacedByAiTexts((prev) => uniqueKeepOrderStrings([...prev, ...selectedSmsTexts]));
      setSelectedSmsTexts(newTexts.slice(0, MAX_SELECTED_TEXTS));
      setPerDropSmsTexts(null);
    },
    [selectedSmsTexts, setLocalTexts, setSelectedSmsTexts],
  );

  return {
    showSenders,
    setShowSenders,
    showTexts,
    setShowTexts,
    pendingHideSenders,
    replacedByAiSenders,
    pendingHideTexts,
    replacedByAiTexts,
    perDropSmsTexts,
    setPerDropSmsTexts,
    perDropSenderNames,
    setPerDropSenderNames,
    markSendersForHide,
    saveGeneratedSendersAndSelectOnly,
    markTextsForHide,
    saveGeneratedAndSelectOnly,
  };
}

export type EditSentDropCreativeAiApi = ReturnType<typeof useEditSentDropCreativeAiState>;
